import { FC, useContext } from 'react';

import { icon_basket } from '@gn-audio/icons';
import { Button, JabraIcon } from '@gn-audio/jss-components/components';
import { useBasket } from '@gn-audio/jss-components/components/Basket/BasketContext/useBasket';
import { TrackingContext } from '@gn-audio/jss-components/utils/contexts';
import { AvailabilityState, GtmEventType, GtmPayload, SegmentType } from '@gn-audio/jss-core';

import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { Attribute, SegmentTypes } from 'api/productCatalogueApi';
import { getAvailabilityState } from 'composables/Product/Buy/customizerHelpers';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { isNotNil } from 'utils/utils';

export interface ProductActionProps {
  price?: StandardPriceModel | null;
  segmentType: SegmentType;
  engravingMessage?: string;
  accessorySkus?: string[];
  productSku?: string;
  productCategory?: string | Attribute;
  productName?: string;
  productBrand?: string | Attribute;
}

export const ProductAction: FC<ProductActionProps> = ({
  price,
  segmentType,
  productSku,
  accessorySkus,
  productCategory,
  productName,
  productBrand,
}) => {
  const { t } = useFallbackTranslation();

  const { addProducts, busy } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);

  const whereToBuyUrl =
    segmentType === SegmentTypes.Business
      ? '/where-to-buy/partner-locator'
      : '/where-to-buy/retailer-locator';

  const eCommercePayload: GtmPayload = {
    event: GtmEventType.AddToCart,
    ecommerce: {
      currency: price?.currency || '',
      items: [
        {
          item_name: productName || '',
          item_id: productSku || '',
          price: price?.discountedPrice || '0.00',
          item_brand: productBrand || '',
          item_category: productCategory || '',
          quantity: '1',
          currency: price?.currency || '',
        },
      ],
    },
  };

  const addToBasket = () => {
    if (!productSku) return;
    const accessoryBasketItems = (accessorySkus ?? []).map((sku) => ({ sku }));
    gtmEvent(eCommercePayload);
    addProducts([{ sku: productSku }, ...accessoryBasketItems]);
  };

  const availability = getAvailabilityState(price);

  return (
    <div>
      {isNotNil(availability) &&
        [AvailabilityState.Add, AvailabilityState.BackOrder, AvailabilityState.PreOrder].includes(
          availability
        ) &&
        price && (
          <Button
            busy={busy}
            className="w-full"
            variant="primary"
            size="l"
            iconPlacement="right"
            icon={<JabraIcon Icon={icon_basket} />}
            onClick={addToBasket}
          >
            {availability === AvailabilityState.Add && (
              <span>{t('system.CustomizerAddToCart')}</span>
            )}
            {availability === AvailabilityState.BackOrder && <span>{t('system.Backorder')}</span>}
            {availability === AvailabilityState.PreOrder && <span> {t('system.Preorder')}</span>}
          </Button>
        )}
      {availability === AvailabilityState.WhereToBuy && (
        <Button
          busy={busy}
          className="w-full"
          variant="primary"
          size="l"
          href={whereToBuyUrl}
          asLink
        >
          <span>{t('system.WhereToBuy')}</span>
        </Button>
      )}
    </div>
  );
};
